import ObjectState from "_src/history/object_state";
import ChunkState from "_src/history/chunk_state";
import PlaneState from "_src/history/plane_state";
import Chunk from "_src/engine/chunk";
import workspace from "_src/workspace";
import cache from "_cacheModule";
import {colorType, stepType} from "_src/constants";
import appState from "_src/app_state";

import events from "_src/engine/events";
import {Geometry, GeometryBuffer} from "_src/engine/geometry";
import output from "_src/engine/output";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import {pixelBufferType} from "_src/engine/constants";
import {colorUtils, idGenerator} from "_src/utils";
import { DEFAULT_ATTRIBUTES } from "_src/engine/utility/attributes";

const ERASE_BUFFER_NAMES  = ["indices", "a_position", "a_normal", "a_selection_mask", "a_uv", "a_color", "a_size"];
const DEFEATURE_BUFFER_NAMES  = ["indices", "a_position", "a_normal", "a_selection_mask", "indices", "a_uv"];
const SELECT_BUFFER_NAMES = ["a_selection_mask"];
const SMOOTH_BUFFER_NAMES = ["a_position", "a_normal"];
const HEAL_TEXTURE_BUFFER_NAMES = ["a_selection_mask"];

let historyLocked = false;

const steps = [];

const modifiedFrames = [];
const modifiedObjects = [];

const WAITING       = 0;
const PREVIOUS_STEP = 1;
const NEXT_STEP     = 2;

let stepToApply = WAITING;

let currentUndo = 0;
let maxUndos = 10;
let currentStepID = 0;
let enabled = true;

class StepData {

  constructor(stepID, type, payload, uuid) {
    this.id = stepID;
    this.uuid = uuid || idGenerator.generateUUID();
    this.type = type;
    this.objectsState = [];
    this.framesState = [];
    this.planeState = null;
    this.payload = payload;
    this.applicable = true;
  }

  async apply() {

    historyLocked = true;
    appState.setStateValue("isApplyingHistoryStep", true);

    const objectsState = this.objectsState;
    const promises = [];

    for (let i = 0; i < objectsState.length; i+=2) {
      const object = objectsState[i];
      const state = objectsState[i+1];
      promises.push(state.apply(object));
    }

    for (let frameState of this.framesState) {
      applyTexture(frameState, promises);
      addNewChunks(frameState, promises);
      deleteOldChunks(frameState);
    }

    try {
      await Promise.all(promises);

      //Note: we need to process update operation after add operation
      const updatePromises = [];
      updateAllExistedChunks(this.framesState, updatePromises);

      await Promise.all(updatePromises);

      updateDataStructures(this.framesState).then(() => {
        if (this.planeState) {
          this.planeState.apply();
        }

        historyLocked = false;
        appState.setStateValue("isApplyingHistoryStep", false);
        events.emit("objects_updated", workspace.getObjects());
      });

      return true;

    } catch (e) {
      output.error(output.DEVELOPERS_ERROR, "Can't apply history step: " + e);
      return false;
    }

  }

  addObjectState(object) {

    let objState = getState(object, this.objectsState);

    if (!objState) {
      objState = new ObjectState();
      this.objectsState.push(object, objState);
    }

    objState.appendData(object);

  }

  setSelectionPlaneState(selector, point, normal) {
    this.planeState = new PlaneState();
    this.planeState.appendData(selector, point, normal);
  }

  addFrameState(frame, chunksIn, chunksUp, chunksOut, buffersNames) {

    let frameState = this.framesState.find(frameState => frameState["frame"] === frame);

    if (!frameState) {
      frameState = {"frame": frame, "in": [], "up": [], "out": [], "texture": null};
      this.framesState.push(frameState);
    }

    const frameStateInd = this.framesState.indexOf(frameState);

    const frameTextureInfo = frame.textureList[0] ? frame.textureList[0][0] : null;
    if (frameTextureInfo) {
      frameState["texture"] = {};
      frameState["texture"].file = frameTextureInfo.path;
      frameState["texture"].channels = frameTextureInfo.channels;
      frameState["texture"].width = frameTextureInfo.width;
      frameState["texture"].height = frameTextureInfo.height;
    }

    for (let chunkUp of chunksUp) {
      const chunk = frame.getChunkByID(chunkUp);
      const buffersToSave = chunk.geometry.isPointCloud ? buffersNames.filter(bufferName => bufferName !== "indices") : buffersNames;

      let chunkState = frameState["up"].find(chunkState => chunkState.chunkID === chunkUp);
      if (!chunkState) {
        const cacheID = this.id + "hist" + frameStateInd + "up" + chunk.getID();
        chunkState = new ChunkState(cacheID, chunk.geometry.checkHasSelectedVertices(), chunkUp, chunk.geometry.bbox);
        frameState["up"].push(chunkState);
      }
      chunkState.appendData(chunk, buffersToSave);

      //if chunk has modified buffers to save
      if (buffersToSave.filter(value => DEFAULT_ATTRIBUTES.includes(value)).length > 0) {
        workspace.markChunkAsUpdated(chunk);
      }
    }

    for (let chunkIn of chunksIn) {
      const chunk = frame.getChunkByID(chunkIn);
      const buffersToSave = chunk.geometry.isPointCloud ? buffersNames.filter(bufferName => bufferName !== "indices") : buffersNames;

      let chunkState = frameState["in"].find(chunkState => chunkState.chunkID === chunkIn);
      if (!chunkState) {
        const cacheID = this.id + "hist" + frameStateInd + "in" + chunk.getID();
        chunkState = new ChunkState(cacheID, chunk.geometry.checkHasSelectedVertices(), chunkIn, chunk.geometry.bbox);
        frameState["in"].push(chunkState);
      }
      chunkState.appendData(chunk, buffersToSave);

    }

    for (let chunkOut of chunksOut) {
      let chunkState = frameState["out"].find(chunkState => chunkState.chunkID === chunkOut);
      if (!chunkState) {
        const cacheID = this.id + "hist" + frameStateInd + "out" + chunkOut;
        chunkState = new ChunkState(cacheID, false, chunkOut, []);
        frameState["out"].push(chunkState);
      }
    }

  }

  clearCache() {
    for (let frameState of this.framesState) {
      for (let chunkState of frameState["in"].concat(frameState["up"]).concat(frameState["out"])){
        chunkState.clearCache();
      }
    }
    this.applicable = false;
  }

  updateCache(chunk) {
    for (let frameState of this.framesState) {
      for (let chunkState of frameState["in"].concat(frameState["up"]).concat(frameState["out"])){
        chunkState.updateCache(chunk);
      }
    }
  }

}

export default {
  setEnabled(isEnabled) {
    enabled = isEnabled;
  },

  writeFrameToCurrentStep({type, frame, chunksIn=[], chunksUp=[], chunksOut=[]}) {
    if (!enabled) {
      return;
    }

    this._addChunksToModifiedFrame(frame, chunksIn, chunksUp, chunksOut);

    const numSteps = this._getNumSteps();
    this._writeFrameToStep(type, frame, chunksIn, chunksUp, chunksOut, numSteps - currentUndo - 1);
  },

  writeObjectToCurrentStep(object) {
    if (!enabled) {
      return;
    }

    if (this._getModifiedObjects().indexOf(object) === -1) {
      this._addModifiedObject(object);
    }

    const numSteps = this._getNumSteps();
    this._writeObjectToStep(object, numSteps - currentUndo - 1);

  },

  writeSelectionPlaneToCurrentStep(selector, point = null, normal = null) {
    if (!enabled) {
      return;
    }

    const numSteps = this._getNumSteps();
    this._writeSelectionPlaneToStep(selector, point, normal, numSteps - currentUndo - 1);
  },

  addStep(type, payload = null, uuid = "") {
    if (!enabled) {
      return;
    }

    const isSelectionPlaneStep = type === stepType.SELECT && payload.type === appState.ST_CUT_OFF_PLANE;
    const isCustomStep = type === stepType.CUSTOM;

    const modifiedObjects = this._getModifiedObjects();

    if (modifiedObjects.length === 0 && modifiedFrames.length === 0 && !isSelectionPlaneStep && !isCustomStep) {
      return;
    }

    this._addStep(type, payload, uuid);

    const step = this._getNumSteps() - currentUndo - 1;
    if (step === 0) {
      output.error(output.DEVELOPERS_ERROR, "Can't add history step. History is empty when adding step");
      return
    }

    while (modifiedObjects.length > 0) {
      const obj = modifiedObjects.pop();
      this._writeObjectToStep(obj, step);
    }

    while (modifiedFrames.length > 0) {
      const modifyInfo = modifiedFrames.pop();

      const checkedUpChunks = [];
      const frame = modifyInfo["frame"];
      for (let chunkUpID of modifyInfo["up"]) {
        const chunkUp = frame.getChunkByID(chunkUpID);
        if (chunkUp.geometry.empty()) {
          //Note: we need to put this chunk to the delete-list
          //we need to insert it to the add-list of the previous step as well

          const prevStepData = steps[step-1];
          const prevStepFrameState = prevStepData.framesState.find(frameState => frameState["frame"] === frame);
          if (!prevStepFrameState) {
            output.error(output.DEVELOPERS_ERROR, "Can't add history step. Modified frame is not present in the previous step");
            continue;
          }

          let prevStepChunkStateIdx;
          for (prevStepChunkStateIdx = 0; prevStepChunkStateIdx < prevStepFrameState["up"].length; prevStepChunkStateIdx++){
            if (prevStepFrameState["up"][prevStepChunkStateIdx].chunkID === chunkUpID) {
              break;
            }
          }

          if (prevStepChunkStateIdx === prevStepFrameState["up"].length) {
            output.error(output.DEVELOPERS_ERROR, "Can't add history step. Modified chunk is not present in the previous step");
            continue;
          }

          const prevStepChunkState = prevStepFrameState["up"].splice(prevStepChunkStateIdx, 1)[0];
          if (!prevStepFrameState["in"].find(chunkState => chunkState.cacheID === prevStepChunkState.cacheID)) {
            prevStepFrameState["in"].push(prevStepChunkState);
          }

          modifyInfo["in"].push(chunkUpID);
        } else {
          checkedUpChunks.push(chunkUpID);
        }
      }

      //Note: that's not a mistake, "in" and "out" chunks swap after step applying
      for (let chunkIn of modifyInfo["in"]) {
        const chunk = frame.getChunkByID(chunkIn);
        frame.deleteChunk(chunk);
      }
      modifyInfo["out"].forEach(chunkOut => workspace.markChunkAsNew(frame.getChunkByID(chunkOut)));

      this._writeFrameToStep(type, modifyInfo["frame"], modifyInfo["out"], checkedUpChunks, modifyInfo["in"], step);
    }

    reportHistoryStack();
  },

  schedulePreviousStepApply() {

    if (historyLocked) {
      return false;
    }

    stepToApply = PREVIOUS_STEP;

    return true;

  },

  scheduleNextStepApply() {

    if (historyLocked) {
      return false;
    }

    stepToApply = NEXT_STEP;

    return true;

  },

  async apply() {
    if (!cache.isAvailable()) return false;

    const step = stepToApply;

    if (step == WAITING || historyLocked) {
      return false;
    }

    stepToApply = WAITING;

    const historyPromise = step === PREVIOUS_STEP ? this._applyPreviousStep() : this._applyNextStep();
    const result = await historyPromise;
    reportHistoryStack();

    return result;
  },

  async _applyPreviousStep() {

    const numSteps = this._getNumSteps();

    if (historyLocked || !appState.getStateValue("editingAllowed")) {
      return false;
    }

    if (currentUndo >= numSteps - 1 || currentUndo >= maxUndos) {
      events.emit("undo", { ...getHistoryStack(), notApplicable: true });
      return false;
    }

    const currStep = numSteps - currentUndo - 1;
    const newStepData = steps[currStep - 1];
    if (!newStepData.applicable) {
      events.emit("undo", { ...getHistoryStack(), notApplicable: true });
      return false;
    }

    ++currentUndo;

    events.emit("undo", getHistoryStack());

    return await newStepData.apply();
  },

  async _applyNextStep() {

    const numSteps = this._getNumSteps();
    if (historyLocked || !appState.getStateValue("editingAllowed")) {
      return false;
    }

    if (currentUndo === 0) {
      events.emit("redo", { ...getHistoryStack(), notApplicable: true });
      return false;
    }

    const currStep = numSteps - currentUndo - 1;
    const newStepData = steps[currStep + 1];
    if (!newStepData.applicable) {
      events.emit("redo", { ...getHistoryStack(), notApplicable: true });
      return false;
    }

    --currentUndo;

    events.emit("redo", getHistoryStack());

    return await newStepData.apply();
  },

  setMaxUndos(newMaxUndos) {
    maxUndos = newMaxUndos;
  },

  restart() {

    for (let step of steps) {
      step.clearCache();
    }

    currentUndo = 0;
    currentStepID = 0;
    steps.length = 0;
    this._resetModifiedObjects();
    this._resetModifiedFrames();

    this._addStep(stepType.NONE, null);
    reportHistoryStack();

  },

  removeCustomStep(uuid) {
    const stepID = steps.findIndex(step => step.uuid === uuid && step.type === stepType.CUSTOM);
    if (stepID < 0) {
      output.error(output.API_ERROR, "Can't remove custom step. There no custom step with uuid " + uuid + " in history");
      return;
    }

    const currentPos = steps.length - currentUndo;
    if (stepID >= currentPos) {
      currentUndo--;
    }

    moveStepState(steps[stepID], steps[stepID-1]);
    steps[stepID].clearCache();
    steps.splice(stepID, 1);
    reportHistoryStack();
  },

  removeSelectionSteps() {
    let toStep = null;
    let fromStep = null;
    for (let i=0; i<steps.length-1;i++) {
      if (steps[i].type === stepType.SELECT && steps[i+1].type !== stepType.SELECT) {
        fromStep = steps[i];
      }
      if (steps[i].type !== stepType.SELECT && steps[i+1].type === stepType.SELECT) {
        toStep = steps[i];
      }

      if (fromStep && toStep) {
        moveStepState(fromStep, toStep);

        fromStep = null;
        toStep = null;
      }
    }

    for (let i=0; i<steps.length;) {
      if (steps[i].type === stepType.SELECT) {
        const currentPos = steps.length - currentUndo;
        if (i >= currentPos) {
          currentUndo--;
        }

        steps[i].clearCache();
        steps.splice(i, 1);
      } else {
        for (const frameState of steps[i].framesState) {
          for (const chunkState of frameState["in"].concat(frameState["up"]).concat(frameState["out"])) {
            chunkState.removeFromCache("a_selection_mask");
          }
        }
        steps[i].planeState = null;

        i++;
      }
    }

    reportHistoryStack();
  },

  getLastConsecutiveSelectionSteps() {
    const activeSelectionSteps = [];
    // start from the next-to-last step
    const startStep = steps.length - currentUndo - 2;
    for (let i = startStep; i >= 0; --i) {
      const step = steps[i];
      if (step.type != stepType.SELECT) {
        break;
      }
      activeSelectionSteps.unshift(step.payload);
    }
    return activeSelectionSteps;
  },

  _writeFrameToStep(type, frame, chunksIn, chunksUp, chunksOut, step) {
    const stepData = steps[step];
    stepData.addFrameState(frame, chunksIn, chunksUp, chunksOut, getBuffersNamesByStepType(type));
  },

  _writeObjectToStep(object, step) {
    const stepData = steps[step];
    stepData.addObjectState(object);
  },

  _writeSelectionPlaneToStep(selector, point, normal, step) {
    const stepData = steps[step];
    stepData.setSelectionPlaneState(selector, point, normal);
  },

  _addStep(type, payload, uuid) {

    const numSteps = this._getNumSteps();

    const currStep = numSteps - currentUndo - 1;

    const stepData = new StepData(currentStepID++, type, payload, uuid);

    if (currentUndo > 0) {

      for (let i = currStep + 1; i < numSteps; ++i) {
        const stepToDrop = steps[i];
        stepToDrop.clearCache();
      }

      steps.splice(-currentUndo, currentUndo, stepData);

    } else {

      if (numSteps >= maxUndos + 1) {
        const stepToDrop = steps[numSteps - maxUndos - 1];
        stepToDrop.clearCache();
      }

      steps.push(stepData);

    }

    currentUndo = 0;

  },

  _getNumSteps() {
    return steps.length;
  },

  _getModifiedObjects() {
    return modifiedObjects;
  },

  _addModifiedObject(object) {
    modifiedObjects.push(object);
  },

  _resetModifiedObjects() {
    modifiedObjects.length = 0;
  },

  _addChunksToModifiedFrame(frame, chunksIn, chunksUp, chunksOut) {
    let modifiedFrame = modifiedFrames.find(modifyInfo => modifyInfo.frame === frame);
    if (!modifiedFrame) {
      modifiedFrame = {"frame": frame, "in": [], "up": [], "out": []};
      modifiedFrames.push(modifiedFrame);
    }

    modifiedFrame["in"] = modifiedFrame["in"].concat(chunksIn);
    modifiedFrame["up"] = modifiedFrame["up"].concat(chunksUp);
    modifiedFrame["out"] = modifiedFrame["out"].concat(chunksOut);
  },

  _resetModifiedFrames() {
    modifiedFrames.length = 0;
  },

}

function applyTexture(frameState, promises) {
  const frame = frameState["frame"];

  const frameTextureInfo = frame.textureList[0] ? frame.textureList[0][0] : null;
  const savedTextureInfo = frameState["texture"];

  if (frameTextureInfo) {

    if (savedTextureInfo) {

      if (frameTextureInfo.path !== savedTextureInfo.file) {
        frameTextureInfo.path = savedTextureInfo.file;
        promises.push(frame.loadTexture());
      }

    } else {

      frame.dropTextures();

    }

  } else {

    if (savedTextureInfo) {

      const textureInfo = {"levels": [savedTextureInfo]};
      frame.addTextureInfo(textureInfo);

      const texture = bufferFactory.create(pixelBufferType.T_RGBA,
        {width: 1, height: 1, immutable: true});

      const textureColor = colorUtils.linToSRGB(frame.model.properties.diffuseColor, new Array(3));
      const textureBuffer = new Uint8Array([0, 0, 0, 255]);

      textureBuffer[0] = colorUtils.floatToByte(textureColor[0]);
      textureBuffer[1] = colorUtils.floatToByte(textureColor[1]);
      textureBuffer[2] = colorUtils.floatToByte(textureColor[2]);

      texture.initMipMapLevelBuffer(0, textureBuffer);

      frame.model.setTexture(texture, 0);
      const shader = frame.model.shader;
      shader.setDirective("COLOR_TYPE", colorType.TEXTURE_UV);

      promises.push(frame.loadTexture());
    }

  }
}

function addNewChunks(frameState, promises) {
  const frame = frameState["frame"];

  for (let chunkState of frameState["in"]) {
    let chunk = frame.getChunkByID(chunkState.chunkID);
    const chunkAlreadyInFrame = Boolean(chunk);
    if (!chunk) {
      chunk = new Chunk(chunkState.chunkID);

      const layout = {};
      for (let info of chunkState.cacheInfo) {
        layout[info.name] = new GeometryBuffer({ buffer : new ArrayBuffer(0), type : info.buffType });
      }
      const newGeometry = new Geometry({
        bbox     : chunkState.chunkBbox,
        layout   : layout,
      });
      chunk.geometry = newGeometry;
    }

    promises.push(chunkState.apply(chunk).then(()=> {
      if (appState.getStateValue("activeTool") === appState.TT_ERASER && !chunk.geometry.hasLayoutItem("a_selection_mask")) {
        chunk.geometry.initSelectionBuffers();
        chunk.markAttributeDirty("a_selection_mask");
      }

      if (!chunkAlreadyInFrame) {
        frame.addChunk(chunk);
      }

      events.emit("update_chunk_octree", chunk);
      events.emit("update_chunk_halfedges", chunk);
      workspace.setNeedsDupliVerticesUpdate(chunk, true);

      // TODO: WS-945
      chunk.updateGlBuffers(frame.model.shader);

    }));
  }
}

function deleteOldChunks(frameState) {
  const frame = frameState["frame"];

  for (let chunkState of frameState["out"]) {
    const chunk = frame.getChunkByID(chunkState.chunkID);
    if (!chunk) {
      continue;
    }

    frame.deleteChunk(chunk, false);
  }
}

function updateAllExistedChunks(framesState, promises) {
  for (let frameState of framesState) {

    const frame = frameState["frame"];

    for (let chunkState of frameState["up"]) {
      const chunk = frame.getChunkByID(chunkState.chunkID);
      if (!chunk) {
        output.error(output.DEVELOPERS_ERROR, "Can't apply history step. Trying to update chunk which is not in frame");
        continue;
      }

      const needsDataStructsUpdate = chunkState.checkCacheEntry("a_position");

      promises.push(chunkState.apply(chunk).then(()=> {

        if (needsDataStructsUpdate) {

          events.emit("update_chunk_octree", chunk);
          events.emit("update_chunk_halfedges", chunk);
          workspace.setNeedsDupliVerticesUpdate(chunk, true);

          // TODO: WS-945
          chunk.updateGlBuffers(frame.model.shader);
        }

      }));
    }
  }
}

async function updateDataStructures(framesState) {
  for (const frameState of framesState) {
    const frame = frameState["frame"];
    workspace._calculateNeighbourChunks(frame.model.chunks);
  }

  const dataStructPromises = [];
  for (const object of workspace.getObjects()) {

    object.setState(appState.GS_OCTREE, false);
    object.setState(appState.GS_HALF_EDGE, false);

    dataStructPromises.push(workspace.applyState(object));

  }

  await Promise.all(dataStructPromises);
}

function getState(targetData, states) {

  const dataInd = states.indexOf(targetData);

  if (dataInd !== -1) {
    return states[dataInd + 1];
  }

  return null;

}

function moveStepState(stepFrom, stepTo) {
  for (const frameState of stepFrom.framesState) {
    let existedFrameState = stepTo.framesState.find(state => state["frame"] === frameState["frame"]);
    if (!existedFrameState) {
      existedFrameState = {"frame": frameState["frame"], "in": [], "up": [], "out": [], "texture": frameState["texture"]};
      stepTo.framesState.push(existedFrameState);
    }

    existedFrameState["in"] = existedFrameState["in"].concat(frameState["in"]);
    existedFrameState["up"] = existedFrameState["up"].concat(frameState["up"]);
    existedFrameState["out"] = existedFrameState["out"].concat(frameState["out"]);
  }
  stepFrom.framesState.length = 0;

  for (const objectState of stepFrom.objectsState) {
    stepTo.objectsState.push(objectState);
  }
  stepFrom.objectsState.length = 0;
}

function getBuffersNamesByStepType(type) {
  switch (type) {
  case stepType.ERASE:
    return ERASE_BUFFER_NAMES;
  case stepType.SELECT:
    return SELECT_BUFFER_NAMES;
  case stepType.SMOOTH:
    return SMOOTH_BUFFER_NAMES;
  case stepType.DEFEATURE:
    return DEFEATURE_BUFFER_NAMES;
  case stepType.HEAL_TEXTURE:
    return HEAL_TEXTURE_BUFFER_NAMES;
  }

  return [];
}

function reportHistoryStack() {
  const historyStack = getHistoryStack();
  events.emit("history", historyStack);
}

function getHistoryStack() {
  const stack = [];
  for (const step of steps) {
    if (step.type !== stepType.NONE && step.applicable) {
      stack.push({"type": step.type, "payload": step.payload, "uuid": step.uuid});
    }
  }
  return {"stack": stack, "position": stack.length - currentUndo};
}
